/**
 * @desc utility to open views inside a popover
 * @example
 * import { popover } from 'modules/popover';
 *
 * // open popover
 * popover.open( payload );
 *
 * // close popover
 * popover.close();
 */
import { random, merge, noop } from 'lodash';

// local dependencies
import { PLACEMENTS } from './popover.constants';
import { messageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';

// default popover payload
const getDefaultPayload = () => {
	return {
		id: random( 1, 999 ),                                   // unique id of popover instance
		compact: false,                                         // set width of popover equal to triggering element
		closeOnOverflow: true,                                  // close popover when trigger goes out of viewport
		referenceElement: document.body,                        // trigger element
		placements: [ PLACEMENTS.BOTTOM, PLACEMENTS.TOP ],      // preferred placements for popover in order
		offset: 0,                                              // offset popover view / formats(x,y): 10, '10,20'
		view: null,                                             // view to be injected inside popover body
		props: {},                                              // props that `view` will receive
		styles: {                                               // styles for popover
			arrowColor: '#fff',
			backgroundColor: '#fff',
			borderRadius: 0,
			backdropOpacity: 0,
			margin: 0,
			boxShadow: undefined,
			border: undefined,
		},
		onOpen: noop,                       // callback function when popover opens
		onClose: noop,                      // callback function when popover closes
		handleForceUpdate: null,            // function having a functional argument to forcefully update popover positions
		outsideClickDisabled: false,	    // hide popover on outiside click
		tooltip: true,                      // show tooltip arrow
		scrollElement: null,                // lock scroll of an element,
		scrollParent: undefined,            // this element if scrolled will hide the popover ( if `hideOnScroll` is `true` )
		hideOnScroll: true,                 // should hide on scroll
	};
};

/**
 * @desc open popover widget
 * @param { { id: string|number, compact: boolean, closeOnOverflow: boolean, referenceElement: HTMLElement, placements:['top'|'bottom'|'left'|'right'], view: JSX.Element, props: {}, styles: { arrowColor: string, backgroundColor: string, borderRadius: number }, onOpen: Function, onClose: Function, handleForceUpdate: Function } } _payload - popover open payload
 */
export const open = ( _payload ) => {
    const payload = merge( {}, getDefaultPayload(), _payload );
    messageBus.trigger( MESSAGE_BUS_EVENTS.OPEN_POPOVER, payload );
};

/**
 * @desc update popover props
 */
export const update = ( props ) => {
    messageBus.trigger( MESSAGE_BUS_EVENTS.UPDATE_POPOVER, props );
};

/**
 * @desc close popover widget
 */
export const close = () => {
    messageBus.trigger( MESSAGE_BUS_EVENTS.CLOSE_POPOVER );
};
